import {api, DateRange, isPaginatedQuery, PaginationQuery, PaginationResponse} from '@services/Api'
import {DEFAULT_POSTS_PER_PAGE, ImageUknown} from "@lib/constants";
import {ElasticSearchBucketResponse, Issue} from "@mwServices/Issue";
import {transformMostValuableResponse} from "@mwServices/User";
import {removeEncodedChars} from "@lib/format";

export interface Series {
  series_id: number;
  series_title: string;
  total: number;
  publisher_id: number;
  publisher: string;
  first_issue_id: number;
  full_image: string;
  format: string;
  year_began: string;
  year_ended: string;
  issue_count :number; //total issues in series.
  overview: {
    value: number; //not used?
    items: number; // total items in collection?
    issues: number; // total issues, not multiple copies, in collection.?
    items_hot: number;
    items_key: number;
    items_rare: number;
    issues_by_number: number;
  },
  most_valuable?: Issue | null;
  fmv?: number | null;
  fmv_updated?: string | null;
  fmv_can_update?: boolean | null;
  fmv_is_stale?: boolean | null;
  series_permalink?: string | null;
  issue_min?: string | null;
  issue_max?: string | null;
}

export interface SeriesResponse {
  series: any[];
  overview: {
    total: number;
    publishers?: ElasticSearchBucketResponse[];
    series_types?: ElasticSearchBucketResponse[];
  };
  pagination?: PaginationResponse;
}

export interface SeriesQuery{
  filters: {
    query?: string;
    series?: number[];
    include_most_valuable?: boolean;
    sort?: number;
    date?: DateRange,
    multipleDateRanges?: DateRange[];
    minimumScore?: number;
    publisherId?: number;
    publishers?: number[];
    formats?: string[];
  },
}

export interface SeriesPaginationQuery extends SeriesQuery, PaginationQuery{}

const transformGetSeriesResponse = (responseData) => {
  return {
    ...responseData,
    series: responseData.series.map(series => ({
      ...series,

      series_id: Number(series.series_id),
      total: Number(series.total),
      publisher_id: Number(series.publisher_id),
      first_issue_id: Number(series.first_issue_id),
      issue_count: Number(series.issue_count),
      overview: {
        value: Number(series.overview.value),
        items: Number(series.overview.items),
        issues: Number(series.overview.issues),
        issues_by_number: Number(series.overview.issues_by_number),
        items_hot: Number(series.overview.items_hot),
        items_key: Number(series.overview.items_key),
        items_rare: Number(series.overview.items_rare),
      },
      most_valuable: transformMostValuableResponse(series.most_valuable),
      fmv: Number(series.fmv),
      fmv_can_update: (String(series.fmv_can_update) === '1'),
      fmv_is_stale: (String(series.fmv_is_stale) === '1'),
    }))
  }
};

const transformGetESSeriesResponse = (responseData) => {
  return {
    ...responseData,
    series: responseData.series.map(series => ({
      series_id: Number(series._id),
      series_title: series._source.post_title,
      total: 0,
      publisher_id: Number(series._source.publisher_id),
      publisher: series._source.publisher_title,
      first_issue_id: Number(series._source.first_issue_id),
      issue_count: Number(series._source.issue_count),
      overview: {
        value: 0,
        items: 0,
        issues: Number(series._source.issue_count),
        issues_by_number: 0,
        items_hot: 0,
        items_key: 0,
        items_rare: 0,
      },
      year_began: series._source.year_began,
      year_ended: series._source.year_ended,
      format: series._source.searchable_format,
      series_permalink: series._source.series_permalink,
      full_image: series._source.full_image && series._source.full_image.length >= 0 ? series._source.full_image : ImageUknown.COVER,
      issue_min: series._source.issues_min && series._source.issues_min !== 'null' ? series._source.issues_min : null,
      issue_max: series._source.issues_max && series._source.issues_max !== 'null' ? series._source.issues_max : null,
    }))
  }
};

const transformGetSeriesByIdResponse = (responseData) => {
  const series = responseData.series;
  return {
    ...responseData,
    series: series ? {
      series_id: Number(series.post_id),
      series_title: removeEncodedChars(series.post_title),
      total: 0,
      publisher_id: Number(series.publisher_id),
      publisher: removeEncodedChars(series.publisher_title),
      first_issue_id: Number(series.first_issue_id),
      issue_count: Number(series.issue_count),
      issues_key: Number(series.issues_key),
      issues_hot: Number(series.issues_hot),
      issues_rare: Number(series.issues_rare),
      overview: {
        value: 0,
        items: 0,
        issues: Number(series.issue_count),
        issues_by_number: 0,
        items_hot: Number(series.issues_hot),
        items_key: Number(series.issues_key),
        items_rare: Number(series.issues_rare),
      },
      year_began: series.year_began,
      year_ended: series.year_ended,
      format: series.searchable_format,
      series_permalink: series.series_permalink,
      full_image: series.full_image && series.full_image.length >= 0 ? series.full_image : ImageUknown.COVER,
      issue_min: series.issues_min && series.issues_min !== 'null' ? series.issues_min : null,
      issue_max: series.issues_max && series.issues_max !== 'null' ? series.issues_max : null,
    } : null,
  }
}

const seriesApi = api.injectEndpoints({
  endpoints: (build) => ({

  // @ts-ignore
    getUserSeries: build.query<SeriesResponse, SeriesQuery | SeriesPaginationQuery>({
      query: (args) => {
        const filters = args ? encodeURIComponent(btoa(JSON.stringify(args.filters))) : '';

        if(isPaginatedQuery(args)) {
          const page = args ? args.page : 1;
          const perPage = args ? args.perPage : DEFAULT_POSTS_PER_PAGE;
          return {
            url: `covr/v1/user/series?filters=${filters}&page=${page}&per_page=${perPage}`,
            method: "GET",
          };
        }
        return {
          url: `covr/v1/user/series?filters=${filters}`,
          method: "GET",
        };
      },
      providesTags: ['Series'],
      transformResponse: transformGetSeriesResponse,
    }),
    getUserSeriesViaElasticSearch: build.query<SeriesResponse, SeriesQuery | SeriesPaginationQuery>({
      query: (args) => {
        const filters = args ? btoa(encodeURIComponent(JSON.stringify(args.filters))) : '';

        if(isPaginatedQuery(args)) {
          const page = args ? args.page : 1;
          const perPage = args ? args.perPage : DEFAULT_POSTS_PER_PAGE;
          return {
            url: `covr/v1/search/series?filters=${filters}&page=${page}&per_page=${perPage}`,
            method: "GET",
          };
        }
        return {
          url: `covr/v1/search/series?filters=${filters}`,
          method: "GET",
        };
      },
      providesTags: ['Series'],
      transformResponse: transformGetESSeriesResponse,
    }),
    refreshAllUserSeriesFmv: build.mutation({
      query: (payload) => {
        return {
          url: `covr/v1/user/series/refresh_value_all`,
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: ['Series'],
    }),
    refreshUserSeriesFmv: build.mutation({
      query: (payload) => {
        return {
          url: `covr/v1/user/series/refresh_value`,
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: ['Series'],
    }),
    getSeriesById: build.query<any, any>({
      query: (args) => {
        const seriesId = args ? args.seriesId : 0;
        return {
          url: `covr/v1/series/by_id?series_id=${seriesId}`,
          method: "GET",
        };
      },
      providesTags: ['Series'],
      transformResponse: transformGetSeriesByIdResponse,
    }),
  }),
});

export const {
  useGetUserSeriesQuery,
  useGetUserSeriesViaElasticSearchQuery,
  useRefreshAllUserSeriesFmvMutation,
  useRefreshUserSeriesFmvMutation,
  useGetSeriesByIdQuery,
} = seriesApi;
